import React, { useState, useEffect, useRef } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { CardPaymentForm } from '../components/checkout/CardPaymentForm';
import { APP_CONFIG } from '../config/constants';


// Status do Pagamento
type PaymentStatus = 'idle' | 'processing' | 'success' | 'error' | 'awaiting_pix';

const PIX_DISCOUNT_PERCENTAGE = 0.15;

export default function CheckoutPage() {
    const { user, token, refreshUser } = useAuth();
    const { cartItems, getCartTotal, clearCart, coupon } = useCart();
    const navigate = useNavigate();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const [selectedMethod, setSelectedMethod] = useState<'wallet' | 'pix' | 'card'>('wallet');
    const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [pixData, setPixData] = useState<{ paymentId: string; qrCode: string; qrCodeBase64: string } | null>(null);
    const [isCopied, setIsCopied] = useState(false);

    const cartTotal = getCartTotal();
    const isPixSelected = selectedMethod === 'pix';
    const discountAmount = isPixSelected ? cartTotal * PIX_DISCOUNT_PERCENTAGE : 0;
    const finalTotal = cartTotal - discountAmount;
    const userBalance = user?.balance ?? 0;
    const hasEnoughBalance = userBalance >= finalTotal;

    // Redireciona se carrinho vazio
    useEffect(() => {
        if (cartItems.length === 0 && paymentStatus === 'idle') {
            navigate('/cart');
        }
    }, [cartItems.length, navigate, paymentStatus]);

    // Limpa intervalo ao desmontar
    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    // Se o saldo for insuficiente, muda para PIX automaticamente
    useEffect(() => {
        if (selectedMethod === 'wallet' && !hasEnoughBalance && user) {
            setSelectedMethod('pix');
        }
    }, [selectedMethod, hasEnoughBalance, user]);

    // ========== PAGAMENTO COM CARTEIRA ==========
    const handleWalletPayment = async () => {
        if (!token || !hasEnoughBalance) return;

        setPaymentStatus('processing');
        setErrorMessage('');

        try {
            const response = await apiClient.post('/checkout/process-payment', {
                paymentType: 'wallet',
                cart: cartItems.map(item => ({ id: item.id, price: item.price, name: item.name })),
                couponCode: coupon?.code
            });

            const result = response.data;

            if (result.status === 'approved') {
                setPaymentStatus('success');
                setSuccessMessage('Compra realizada com sucesso! Redirecionando...');
                refreshUser();
                clearCart();
                setTimeout(() => navigate('/conta'), 2000);
            } else {
                throw new Error(result.message || 'Pagamento não aprovado');
            }
        } catch (error: any) {
            setPaymentStatus('error');
            setErrorMessage(error.response?.data?.error || error.message || 'Erro ao processar pagamento');
        }
    };

    // ========== PAGAMENTO COM PIX ==========
    const handlePixPayment = async () => {
        if (!token) return;

        setPaymentStatus('processing');
        setErrorMessage('');

        try {
            const response = await apiClient.post('/checkout/process-payment', {
                paymentType: 'pix',
                cart: cartItems.map(item => ({ id: item.id, price: item.price, name: item.name })),
                couponCode: coupon?.code
            });

            const result = response.data;

            setPixData(result);
            setPaymentStatus('awaiting_pix');

            // Inicia polling para verificar status
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => checkPixStatus(result.paymentId), 5000);
        } catch (error: any) {
            setPaymentStatus('error');
            setErrorMessage(error.response?.data?.error || error.message || 'Erro ao gerar código PIX');
        }
    };

    const checkPixStatus = async (paymentId: string) => {
        if (!token) return;

        try {
            const response = await apiClient.post('/checkout/payment-status', { paymentId });
            const result = response.data;

            if (result.status === 'approved') {
                if (intervalRef.current) clearInterval(intervalRef.current);
                setPaymentStatus('success');
                setSuccessMessage('Pagamento PIX confirmado! Redirecionando...');
                refreshUser();
                clearCart();
                setTimeout(() => navigate('/conta'), 2000);
            }
        } catch (error) {
            console.error('Erro ao verificar status PIX:', error);
        }
    };

    const handleCopyPixCode = () => {
        if (!pixData) return;
        const cleanedQrCode = pixData.qrCode;
        navigator.clipboard.writeText(cleanedQrCode);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    // ========== PAGAMENTO COM CARTÃO ==========
    const handleCardPayment = async (cardPaymentData: any) => {
        if (!token) return;

        setPaymentStatus('processing');
        setErrorMessage('');

        try {
            const response = await apiClient.post('/checkout/process-payment', {
                paymentType: 'card',
                cart: cartItems.map(item => ({ id: item.id, price: item.price, name: item.name })),
                couponCode: coupon?.code,
                ...cardPaymentData
            });

            const result = response.data;

            if (result.status === 'approved') {
                setPaymentStatus('success');
                setSuccessMessage('Pagamento aprovado! Redirecionando...');
                refreshUser();
                clearCart();
                setTimeout(() => navigate('/conta'), 2000);
            } else if (result.status === 'pending') {
                setPaymentStatus('success');
                setSuccessMessage(result.message || 'Pagamento em análise. Você será notificado quando for aprovado.');
                clearCart();
                setTimeout(() => navigate('/conta'), 3000);
            } else {
                throw new Error(result.message || 'Pagamento não aprovado');
            }
        } catch (error: any) {
            setPaymentStatus('error');
            setErrorMessage(error.response?.data?.error || error.response?.data?.details || error.message || 'Erro ao processar cartão');
        }
    };

    if (cartItems.length === 0 && paymentStatus !== 'success') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
                <div className="text-center">
                    <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Carrinho Vazio</h1>
                    <p className="text-gray-600 mb-6">Adicione plugins ao carrinho para continuar</p>
                    <Link to="/plugins" className="inline-block bg-brand-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                        Ver Plugins
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50 py-12">
            <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-4xl font-extrabold text-gray-900 mb-2">Finalizar Compra</h1>
                        <p className="text-gray-600">Complete seu pedido de forma rápida e segura</p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6">
                        {/* Coluna Principal - Métodos de Pagamento */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                                {finalTotal > 0 ? (
                                    <>
                                        <h2 className="text-xl font-bold text-gray-900 mb-6">Método de Pagamento</h2>

                                        {/* Métodos de Pagamento */}
                                        <div className="space-y-3 mb-6">
                                            {/* Carteira */}
                                            <button
                                                onClick={() => setSelectedMethod('wallet')}
                                                disabled={!hasEnoughBalance}
                                                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                                                    selectedMethod === 'wallet'
                                                        ? 'border-brand-blue bg-blue-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                } ${!hasEnoughBalance ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                            selectedMethod === 'wallet' ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-gray-900">Saldo da Carteira</p>
                                                            <p className="text-sm text-gray-500">
                                                                Disponível: R$ {userBalance.toFixed(2).replace('.', ',')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {selectedMethod === 'wallet' && (
                                                        <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </button>

                                            {/* PIX */}
                                            <button
                                                onClick={() => setSelectedMethod('pix')}
                                                className={`w-full p-4 rounded-xl border-2 transition-all text-left cursor-pointer ${
                                                    selectedMethod === 'pix'
                                                        ? 'border-brand-blue bg-blue-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                            selectedMethod === 'pix' ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-gray-900">PIX</p>
                                                            <p className="text-sm text-green-600 font-semibold">15% de desconto!</p>
                                                        </div>
                                                    </div>
                                                    {selectedMethod === 'pix' && (
                                                        <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </button>

                                            {/* Cartão */}
                                            <button
                                                onClick={() => setSelectedMethod('card')}
                                                className={`w-full p-4 rounded-xl border-2 transition-all text-left cursor-pointer ${
                                                    selectedMethod === 'card'
                                                        ? 'border-brand-blue bg-blue-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                            selectedMethod === 'card' ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-gray-900">Cartão de Crédito</p>
                                                            <p className="text-sm text-gray-500">Aprovação instantânea</p>
                                                        </div>
                                                    </div>
                                                    {selectedMethod === 'card' && (
                                                        <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </button>
                                        </div>

                                        {/* Área de Ação/Conteúdo do Pagamento */}
                                        <div className="border-t border-gray-200 pt-6">
                                            {/* Estado de Sucesso */}
                                            {paymentStatus === 'success' && (
                                                <div className="text-center py-8">
                                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                        <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                    <h3 className="text-xl font-bold text-gray-900 mb-2">Pagamento Confirmado!</h3>
                                                    <p className="text-gray-600">{successMessage}</p>
                                                </div>
                                            )}

                                            {/* Estado de Erro */}
                                            {paymentStatus === 'error' && (
                                                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                                                    <div className="flex items-start gap-3">
                                                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <div>
                                                            <p className="font-semibold text-red-900">Erro no Pagamento</p>
                                                            <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Botões de Ação */}
                                            {paymentStatus === 'idle' && selectedMethod === 'wallet' && (
                                                <button
                                                    onClick={handleWalletPayment}
                                                    disabled={!hasEnoughBalance}
                                                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold py-4 rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
                                                >
                                                    Finalizar Compra com Carteira
                                                </button>
                                            )}

                                            {paymentStatus === 'idle' && selectedMethod === 'pix' && (
                                                <button
                                                    onClick={handlePixPayment}
                                                    className="w-full bg-gradient-to-r from-brand-blue to-blue-600 text-white font-bold py-4 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                                >
                                                    Gerar Código PIX
                                                </button>
                                            )}

                                            {paymentStatus === 'idle' && selectedMethod === 'card' && (
                                                <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                                                    <h3 className="font-bold text-lg text-gray-900 mb-4">Dados do Cartão</h3>
                                                    <CardPaymentForm
                                                        amount={finalTotal}
                                                        onSubmit={handleCardPayment}
                                                        isProcessing={paymentStatus !== 'idle'}
                                                        userEmail={user?.email || ''}
                                                    />
                                                </div>
                                            )}

                                            {paymentStatus === 'processing' && (
                                                <div className="text-center py-8">
                                                    <svg className="animate-spin h-12 w-12 text-brand-blue mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <p className="text-gray-600 font-semibold">Processando pagamento...</p>
                                                </div>
                                            )}

                                            {/* Exibição do QR Code PIX */}
                                            {paymentStatus === 'awaiting_pix' && pixData && (
                                                <div className="space-y-6">
                                                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 text-center border border-blue-200">
                                                        <h3 className="font-bold text-lg text-gray-900 mb-4">
                                                            <svg className="w-8 h-8 text-brand-blue mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                                            </svg>
                                                            Escaneie o QR Code com seu aplicativo de pagamento
                                                        </h3>
                                                        <div className="bg-white p-6 rounded-xl inline-block shadow-lg">
                                                            {pixData.qrCodeBase64 ? (
                                                                <img
                                                                    src={`data:image/png;base64,${pixData.qrCodeBase64}`}
                                                                    alt="QR Code PIX"
                                                                    className="w-64 h-64 mx-auto"
                                                                />
                                                            ) : (
                                                                <div className="w-64 h-64 bg-gray-100 rounded flex items-center justify-center">
                                                                    <svg className="animate-spin h-12 w-12 text-brand-blue" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-gray-600 mt-4">
                                                            Use qualquer app que aceita PIX: Nubank, PicPay, Banco Inter, etc.
                                                        </p>
                                                    </div>

                                                    <div className="bg-white rounded-xl p-6 border border-gray-200">
                                                        <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                                            <svg className="w-5 h-5 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                            </svg>
                                                            Ou copie e cole o código PIX:
                                                        </label>
                                                        <div className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                value={pixData.qrCode}
                                                                readOnly
                                                                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-xs font-mono overflow-x-auto"
                                                            />
                                                            <button
                                                                onClick={handleCopyPixCode}
                                                                className={`px-6 py-3 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 ${
                                                                    isCopied
                                                                        ? 'bg-green-600 text-white'
                                                                        : 'bg-brand-blue text-white hover:bg-blue-700'
                                                                }`}
                                                            >
                                                                {isCopied ? (
                                                                    <span className="flex items-center gap-2">
                                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                        Copiado!
                                                                    </span>
                                                                ) : (
                                                                    <span className="flex items-center gap-2">
                                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                        </svg>
                                                                        Copiar
                                                                    </span>
                                                                )}
                                                            </button>
                                                        </div>
                                                        <p className="text-xs text-gray-500 mt-2">
                                                            Cole este código no seu app de pagamento na opção "Pix Copia e Cola"
                                                        </p>
                                                    </div>

                                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5">
                                                        <div className="flex items-start gap-3">
                                                            <svg className="animate-spin h-6 w-6 text-brand-blue flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            <div className="flex-1">
                                                                <p className="font-bold text-brand-blue mb-1">
                                                                    Aguardando confirmação do pagamento...
                                                                </p>
                                                                <p className="text-sm text-gray-600">
                                                                    Assim que você pagar, seu pedido será processado automaticamente
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                                        <div className="flex items-start gap-3">
                                                            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <div>
                                                                <p className="text-sm font-semibold text-yellow-900">Importante:</p>
                                                                <p className="text-sm text-yellow-700 mt-1">
                                                                    O QR Code expira em 30 minutos. Após o pagamento, seus plugins estarão disponíveis imediatamente.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-center py-6 bg-green-50 border border-green-200 rounded-xl mb-6">
                                            <svg className="w-12 h-12 text-green-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <h3 className="text-lg font-bold text-green-900">Seu pedido é gratuito!</h3>
                                            <p className="text-sm text-green-700 mt-1">
                                                Não será necessário realizar nenhum pagamento para concluir esta compra.
                                            </p>
                                        </div>

                                        {/* Área de Ação/Conteúdo do Pagamento para Compra Grátis */}
                                        <div className="pt-2">
                                            {/* Estado de Sucesso */}
                                            {paymentStatus === 'success' && (
                                                <div className="text-center py-8">
                                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                        <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                    <h3 className="text-xl font-bold text-gray-900 mb-2">Compra Concluída!</h3>
                                                    <p className="text-gray-600">{successMessage}</p>
                                                </div>
                                            )}

                                            {/* Estado de Erro */}
                                            {paymentStatus === 'error' && (
                                                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                                                    <div className="flex items-start gap-3">
                                                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <div>
                                                            <p className="font-semibold text-red-900">Erro ao Processar</p>
                                                            <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {paymentStatus === 'idle' && (
                                                <button
                                                    onClick={handleWalletPayment}
                                                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold py-4 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                                >
                                                    Resgatar Plugin Grátis
                                                </button>
                                            )}

                                            {paymentStatus === 'processing' && (
                                                <div className="text-center py-8">
                                                    <svg className="animate-spin h-12 w-12 text-brand-blue mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <p className="text-gray-600 font-semibold">Processando pedido...</p>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Sidebar - Resumo do Pedido */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 sticky top-6">
                                <h2 className="text-xl font-bold text-gray-900 mb-6">Resumo do Pedido</h2>

                                {/* Lista de Itens */}
                                <div className="space-y-3 mb-6">
                                    {cartItems.map((item) => (
                                        <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-blue/30 transition-colors">
                                            {item.iconUrl ? (
                                                <img
                                                    src={`${APP_CONFIG.uploadsBaseUrl}${item.iconUrl}`}
                                                    alt={item.name}
                                                    className="relative w-20 h-20 rounded-xl bg-white object-contain p-2 shadow-lg ring-2 ring-gray-100 group-hover:ring-brand-blue/50 transition-all"
                                                />
                                            ) : (
                                                <div className="w-14 h-14 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg flex items-center justify-center shadow-sm border border-gray-200">
                                                    <svg className="w-7 h-7 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                    </svg>
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-gray-900 text-sm truncate">{item.name}</p>
                                                <p className="text-sm font-medium text-brand-blue">R$ {item.price.toFixed(2).replace('.', ',')}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Valores */}
                                <div className="space-y-3 border-t border-gray-200 pt-4">
                                    <div className="flex justify-between text-gray-600">
                                        <span>Subtotal</span>
                                        <span>R$ {cartTotal.toFixed(2).replace('.', ',')}</span>
                                    </div>
                                    
                                    {isPixSelected && discountAmount > 0 && (
                                        <div className="flex justify-between text-green-600 font-semibold">
                                            <span>Desconto PIX (15%)</span>
                                            <span>- R$ {discountAmount.toFixed(2).replace('.', ',')}</span>
                                        </div>
                                    )}

                                    <div className="flex justify-between text-xl font-bold text-gray-900 pt-3 border-t border-gray-200">
                                        <span>Total</span>
                                        <span>R$ {finalTotal.toFixed(2).replace('.', ',')}</span>
                                    </div>
                                </div>

                                {/* Informações de Segurança */}
                                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                        </svg>
                                        <div>
                                            <p className="text-sm font-semibold text-green-900">Compra Segura</p>
                                            <p className="text-xs text-green-700 mt-1">
                                                Seus dados estão protegidos com criptografia de ponta a ponta
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
